import React, { useState } from 'react';
import {
  Box, Typography, SvgIconProps, useTheme, useMediaQuery,
} from '@mui/material';
import {
  blueGrey, common, grey, lightBlue, orange, green, yellow,
} from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { pascalCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/PlaylistAddCheck';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { PartnerDescription } from './models/PartnerDescription';
import { getCanonicalPartnerName } from './constants';
import { useCustomEventTracking } from './useCustomEventTracking';
import { usePartnerEnrollmentDialog } from '../components/Dashboard/Partner/IndividualPages/usePartnerEnrollmentDialog';
import { PartnerEnrollmentStatus } from './enums';
import { addUserInitiatedHomeDepotEnrollmentParam } from './homeDepotEnrollmentIntent';
import { homeDepotPartnerId } from './utils/PartnerEnrollmentUtils';
import OptimusButton from './library/Button/Button';
import { RebateType } from './models/Partner';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectMemberInformation } from '../store/slices/onboardingSlice';
import {
  MarkPartnerSalesContacted,
  selectContactedSalesPartnerIds,
} from '../store/slices/dashboardSlice';
import { SubscriptionStatus } from './models/Subscription';
import { isPaidTier } from './utils/MembershipTierAccess';
import { useNavigationUtils } from './utils/useNavigationUtils';
import { useServiceNationUrl } from './utils/useServiceNationUrl';
import { useIsDesktop } from './hooks/useResponsive';
import { useUpgradeNavigation } from './hooks/useUpgradeNavigation';
import { ContactSalesDialog } from '../components/Dashboard/Partner/IndividualPages/ContactSalesDialog';
import * as dashboardAPI from '../api/dashboardAPI';

interface PartnerCardProps {
  PartnerLogo: React.ComponentType;
  partnerInfo: PartnerDescription;
  showEnrollmentStatus?: boolean;
}

export function PartnerCard({
  PartnerLogo, partnerInfo, showEnrollmentStatus = false,
}: PartnerCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackPartnerEnrollment, trackEngagement } = useCustomEventTracking();
  const { openExternalLink } = useNavigationUtils();
  const { buildServiceNationUrl } = useServiceNationUrl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { navigateToUpgrade } = useUpgradeNavigation();
  const {
    openPartnerModal,
    PartnerDialog,
    updatePartnerOpenState,
  } = usePartnerEnrollmentDialog(partnerInfo.id);

  // SN-838: derive analytics event names from the rename-stable canonical name
  // (keyed off the partner code) so a Retool rename does not fork the funnels.
  const partnerKeyName = getCanonicalPartnerName(partnerInfo.partnerCode, partnerInfo.partnerName);

  const [openContactSales, setOpenContactSales] = useState(false);
  // Kept in the (persisted) store rather than local state so the disabled CTA survives a
  // reload and stays in sync with the partner's profile page (SN-874).
  const contactedSalesPartnerIds = useAppSelector(selectContactedSalesPartnerIds);
  const hasContactedSales = contactedSalesPartnerIds.includes(partnerInfo.id);

  // A required form replaces the enrollment call to action for a member who can already
  // reach the partner; it does not override the tier gate, so a locked partner still shows
  // Contact Sales or Upgrade. See the "Partners with a required form" chart on product's
  // "SN Logic on the Edge platform" page (SN-996).
  //
  // Home Depot is excluded even when unlocked: it is configured with a required form but
  // owns its own enrollment call to action, the Add Rewards redirect into the Home Depot
  // flow.
  const showRequiredForm = !!partnerInfo.hasRequiredForm &&
    partnerInfo.id !== homeDepotPartnerId;

  const canAddRewards = partnerInfo.enrollment === undefined ||
    partnerInfo.enrollment === PartnerEnrollmentStatus.NotStarted;
  const isDesktop = useIsDesktop();
  const isLargeScreen = useMediaQuery('(min-width:1400px)');
  const showIcon = !isDesktop || isLargeScreen;
  const { company } = useAppSelector(selectMemberInformation);

  const subscription = company?.subscription;
  const isSubscriptionActive = subscription?.subscriptionStatus === SubscriptionStatus.ACTIVE;
  // Reads the shared paid/free chart rather than testing against Edge inline: the two agree
  // today, but "paid" is a product decision that already moved once (Association), and
  // isPaidTier is the one place it is recorded (SN-996).
  const isPaidSubscription = isPaidTier(subscription?.subscriptionTier);
  const shouldShowRebateBadge =
    isSubscriptionActive && isPaidSubscription && !!partnerInfo.maxRebate;
  // Product (SN-996): only a free Edge member sees the Paid plan tag. A partner locked
  // for a free member is always a Service Nation (paid tier) one, since Edge partners are
  // free. Paid members, even on a partner above their tier, never see the tag.
  const shouldShowPaidPlanBadge = !!partnerInfo.isLocked && !isPaidSubscription;

  const onPartnerCardClick = () => {
    if (partnerInfo?.partnerProfileLink) {
      navigate(partnerInfo.partnerProfileLink, {
        state: { fromTab: showEnrollmentStatus ? 'your-partners' : 'all-partners' },
      });
    }
  };

  const PartnerBadge = ({
    icon: Icon,
    label,
    colors,
    dataTestId,
  }: {
    icon?: React.ComponentType<SvgIconProps>,
    label: string,
    colors: {
      border: string,
      background: string,
      text: string,
    },
    dataTestId?: string,
  }) => (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        padding: `${theme.spacing(0.5)} ${theme.spacing(1.25)}`,
        alignItems: 'center',
        gap: theme.spacing(0.5),
        borderRadius: theme.spacing(3),
        border: `1px solid ${colors.border}`,
        background: colors.background,
        lineHeight: theme.spacing(2.5),
        width: 'fit-content',
      }}
    >
      {Icon && <Icon sx={{ fontSize: theme.spacing(2), color: colors.text }} />}
      <Typography variant='h10' sx={{ color: colors.text }}>
        {label}
      </Typography>
    </Box>
  );

  const formatRebateLabel = () => {
    if (!partnerInfo.maxRebate) return '';
    const { value, type } = partnerInfo.maxRebate;
    return type === RebateType.PERCENTAGE ?
      `${t('partners.rebateBadge.upTo')} ${value}%` :
      `${t('partners.rebateBadge.upTo')} $${value}`;
  };

  const badgeConfig = {
    paidPlan: {
      label: t('partners.paidPlanBadge'),
      colors: {
        border: yellow[700],
        background: yellow[50],
        text: yellow[900],
      },
      dataTestId: `${partnerInfo.dataTestId}-paid-plan-badge`,
    },
    popular: {
      label: t('partners.popularBadge'),
      colors: {
        border: lightBlue[100],
        background: lightBlue[50],
        text: lightBlue[800],
      },
      dataTestId: `${partnerInfo.dataTestId}-popular-badge`,
    },
    new: {
      label: t('partners.newBadge'),
      colors: {
        border: blueGrey[200],
        background: blueGrey[50],
        text: blueGrey[700],
      },
      dataTestId: `${partnerInfo.dataTestId}-new-badge`,
    },
    rebate: {
      label: formatRebateLabel(),
      colors: {
        border: blueGrey[200],
        background: blueGrey[50],
        text: blueGrey[700],
      },
      dataTestId: `${partnerInfo.dataTestId}-rebate-badge`,
    },
    actionRequired: {
      label: t('partnerDetails.enrollmentStatus.actionRequired'),
      colors: {
        border: orange[200],
        background: orange[50],
        text: orange[900],
      },
      dataTestId: `${partnerInfo.dataTestId}-action-required-badge`,
    },
    inProgress: {
      label: t('partnerDetails.enrollmentStatus.inProgress'),
      colors: {
        border: grey[300],
        background: grey[50],
        text: grey[700],
      },
      dataTestId: `${partnerInfo.dataTestId}-in-progress-badge`,
    },
    completed: {
      label: t('partnerDetails.enrollmentStatus.earningRewards'),
      colors: {
        border: green[200],
        background: green[50],
        text: green[800],
      },
      dataTestId: `${partnerInfo.dataTestId}-earning-rewards-badge`,
    },
  };

  const handleAddRewardsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackPartnerEnrollment(
      'Open',
      `${pascalCase(partnerKeyName)}SignupForm`,
    );
    // Home Depot has a different enrollment flow using redirectPath. Mark the navigation as
    // user-initiated so useCompanyEnrollmentInfo lets it land on the partner profile page
    // instead of forcing the full /onboarding/setuphomedepot/web flow (SN-924).
    if (partnerInfo.redirectPath) {
      partnerInfo.onNavigate?.();
      navigate(addUserInitiatedHomeDepotEnrollmentParam(partnerInfo.redirectPath), {
        state: { userInitiatedHdEnrollment: true },
      });
    } else {
      updatePartnerOpenState(true);
    }
  };

  const handleUpgradeNowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEngagement('Navigation', 'Click', 'UpgradeNowCard', {
      partnerName: partnerKeyName,
      isOptimizelyEvent: true,
    });
    navigateToUpgrade();
  };

  // Advanced/Alliance partners cannot be unlocked by an in-app upgrade, so the CTA opens the
  // same Contact sales confirmation modal the profile page shows (SN-874).
  const handleContactSalesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEngagement('Navigation', 'Click', 'ContactSalesCard', {
      partnerName: partnerKeyName,
      isOptimizelyEvent: true,
    });
    setOpenContactSales(true);
  };

  const handleContactSalesConfirm = async () => {
    if (!company?.id) return;
    trackEngagement('Navigation', 'Submit', 'ContactSalesCard', {
      partnerName: partnerKeyName,
      isOptimizelyEvent: true,
    });
    try {
      await dashboardAPI.requestContactSales(company.id, partnerInfo.id);
      setOpenContactSales(false);
      dispatch(MarkPartnerSalesContacted(partnerInfo.id));
    } catch (error) {
      // Keep the modal open so the member can retry.
      // eslint-disable-next-line no-console
      console.error('Failed to send contact sales request', error);
    }
  };

  const handleRequiredFormClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    trackPartnerEnrollment(
      'Click',
      `${pascalCase(partnerKeyName)}RequiredForm`,
    );
    if (partnerInfo.requiredFormSsoDestination) {
      try {
        openExternalLink(await buildServiceNationUrl(partnerInfo.requiredFormSsoDestination));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to generate Service Nation SSO token', error);
      }
    } else if (partnerInfo.requiredFormUrl) {
      openExternalLink(partnerInfo.requiredFormUrl);
    } else {
      // eslint-disable-next-line no-console
      console.error('No URL or SSO destination found for required form');
    }
  };

  return (
    <>
      <Box
        data-testid={partnerInfo.dataTestId}
        onClick={() => {
          trackPartnerEnrollment(
            'Click',
            `${pascalCase(partnerKeyName)}PartnerProfile`,
          );
          onPartnerCardClick();
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`,
          borderRadius: theme.spacing(1.5),
          backgroundColor: common.white,
          border: `${theme.spacing(0.125)} solid ${grey[200]}`,
          boxShadow: '0 1.25px 2.5px 0 rgba(10, 13, 18, 0.05)',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-image 0.15s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 4px 8px 0 rgba(10, 13, 18, 0.1)',
            transform: 'translateY(-2px)',
          },
          // Skip the card gradient while an inner CTA is hovered/pressed so only one darkens
          '&:hover:not(:has(button:hover))': {
            backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
          },
          '&:active:not(:has(button:active))': {
            backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
          },
        }}
      >
        <Box
          className='partner-header-container'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            mb: theme.spacing(1.5),
            gap: theme.spacing(1),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              gap: theme.spacing(1),
            }}
          >
            {/* Logo */}
            <Box
              className='partner-logo-container'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <PartnerLogo />
            </Box>
            {/* Name */}
            <Typography
              data-testid={`${partnerInfo.dataTestId}-name`}
              variant="bodyMdProminent"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {partnerInfo.title}
            </Typography>
            {/* Badges stacked vertically, aligned to the right -
            Paid plan takes priority on top, then Popular/New (All Partners tab only),
            then enrollment status (Your Partners tab only) */}
            {(shouldShowPaidPlanBadge ||
              (!showEnrollmentStatus &&
                (partnerInfo?.isPopularPartner || partnerInfo?.isNewPartner)) ||
              (showEnrollmentStatus &&
                partnerInfo.enrollment &&
                partnerInfo.enrollment !== PartnerEnrollmentStatus.NotStarted)) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(0.5),
                  ml: 'auto',
                  alignItems: 'flex-end',
                  minWidth: theme.spacing(9.375),
                }}
              >
                {shouldShowPaidPlanBadge && <PartnerBadge {...badgeConfig.paidPlan} />}
                {!showEnrollmentStatus && partnerInfo?.isPopularPartner && (
                  <PartnerBadge {...badgeConfig.popular} />
                )}
                {!showEnrollmentStatus && partnerInfo?.isNewPartner && (
                  <PartnerBadge {...badgeConfig.new} />
                )}
                {showEnrollmentStatus &&
                  partnerInfo.enrollment === PartnerEnrollmentStatus.ActionRequired && (
                  <PartnerBadge {...badgeConfig.actionRequired} />
                )}
                {showEnrollmentStatus &&
                  partnerInfo.enrollment === PartnerEnrollmentStatus.InProgress && (
                  <PartnerBadge {...badgeConfig.inProgress} />
                )}
                {showEnrollmentStatus &&
                  partnerInfo.enrollment === PartnerEnrollmentStatus.Completed && (
                  <PartnerBadge {...badgeConfig.completed} />
                )}
              </Box>
            )}
          </Box>
          {/* Rebate Badge - only shown for non-EDGE tier members with active subscription */}
          {shouldShowRebateBadge && (
            <PartnerBadge {...badgeConfig.rebate} />
          )}
        </Box>
        {/* Partner Description */}
        <Box
          className='partner-description-container'
          sx={{
            width: '100%',
            textAlign: 'left',
            mb: theme.spacing(2),
            flex: 1,
          }}
        >
          <Typography
            variant="labelSm"
            sx={{
              color: grey[700],
              lineHeight: theme.spacing(2.5),
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(1.75),
                lineHeight: theme.spacing(2.5),
              },
            }}
          >
            {partnerInfo.description}
          </Typography>
        </Box>
        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(1),
            alignItems: 'center',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
            },
          }}
        >
          {partnerInfo.isLocked && partnerInfo.requiresSalesContact && (
            <OptimusButton
              data-testid={`${partnerInfo.dataTestId}-contact-sales`}
              customVariant="primary"
              size="s"
              leadingIcon={<LockOutlinedIcon />}
              onClick={handleContactSalesClick}
              disabled={hasContactedSales}
              buttonText={t('partnerDetails.contactSales.cta') || 'Contact Sales'}
              fullWidth
              sx={{
                width: '50%',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
              minWidth="auto"
            />
          )}
          {partnerInfo.isLocked && !partnerInfo.requiresSalesContact && (
            <OptimusButton
              data-testid={`${partnerInfo.dataTestId}-upgrade-now`}
              customVariant="primary"
              size="s"
              leadingIcon={<LockOutlinedIcon />}
              onClick={handleUpgradeNowClick}
              buttonText={t('button.upgradeNow') || ''}
              fullWidth
              sx={{
                width: '50%',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
              minWidth="auto"
            />
          )}
          {!partnerInfo.isLocked && isPaidSubscription && showRequiredForm && (
            <OptimusButton
              data-testid={`${partnerInfo.dataTestId}-required-form`}
              customVariant="primary"
              size="s"
              leadingIcon={showIcon ? <ChecklistIcon /> : undefined}
              onClick={handleRequiredFormClick}
              buttonText={t('button.requiredForm') || ''}
              fullWidth
              sx={{
                width: '50%',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
              minWidth="auto"
            />
          )}
          {!partnerInfo.isLocked && !isPaidSubscription && canAddRewards && (
            <OptimusButton
              data-testid={`${partnerInfo.dataTestId}-add-rewards`}
              customVariant="primary"
              size="s"
              leadingIcon={<AddIcon />}
              onClick={handleAddRewardsClick}
              buttonText={t('button.AddRewards') || ''}
              fullWidth
              sx={{
                width: '50%',
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                },
              }}
              minWidth="auto"
            />
          )}
          <OptimusButton
            data-testid={`${partnerInfo.dataTestId}-explore-perks`}
            customVariant="secondaryGray"
            size="s"
            buttonText={t('button.explorePerks') || ''}
            fullWidth
            sx={{
              width: partnerInfo.isLocked ||
                (isPaidSubscription && showRequiredForm) ||
                (!isPaidSubscription && canAddRewards) ? '50%' : '100%',
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            }}
            minWidth="auto"
          />
        </Box>
      </Box>
      {openPartnerModal && PartnerDialog}
      {/* Stop propagation so interacting with the modal doesn't trigger the card's
          navigate-to-profile click handler (SN-874). */}
      {partnerInfo.requiresSalesContact && (
        <Box onClick={(e) => e.stopPropagation()}>
          <ContactSalesDialog
            open={openContactSales}
            onClose={() => setOpenContactSales(false)}
            onConfirm={handleContactSalesConfirm}
            partnerName={partnerInfo.partnerName}
            partnerCode={partnerInfo.partnerCode}
          />
        </Box>
      )}
    </>
  );
}
